import React, { ReactNode } from 'react';
import { ITooltip, Tooltip as ReactTooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import Utils from '../../lib/Utils';
import { Icon, IconTypes } from '../Icon';

interface IIconFill {
  default: string;
  hover?: string;
}

interface IProps extends ITooltip {
  children?: ReactNode;
  className?: string;
  dataCy?: string;
  icon?: IconTypes;
  iconFill?: IIconFill;
  iconMarginLeft?: number;
  iconMarginRight?: number;
  iconSize?: number;
  trigger?: ReactNode;
}

interface IIconProps {
  $iconFill?: IIconFill;
  $iconMarginLeft?: number;
  $iconMarginRight?: number;
  $iconSize?: number;
}

const StyledIcon = styled(Icon)<IIconProps>`
  display: inline-flex;
  align-self: center;
  width: ${toRem(16.5)};
  height: ${toRem(16.5)};
  margin-left: ${toRem(3.75)};
  fill: ${theme.color.neutral[400]};
  cursor: pointer;
  transition: fill ${theme.transition.icon};

  &:hover,
  &:focus {
    fill: ${theme.color.neutral[700]};
  }

  ${({ $iconFill }) =>
    $iconFill &&
    css`
      fill: ${$iconFill.default};

      &:hover,
      &:focus {
        fill: ${$iconFill.hover ? $iconFill.hover : $iconFill.default};
      }
    `}

  ${({ $iconSize }) =>
    $iconSize &&
    css`
      width: ${toRem($iconSize)};
      height: ${toRem($iconSize)};
    `}

  ${({ $iconMarginLeft }) =>
    Number.isFinite($iconMarginLeft) &&
    css`
      margin-left: ${toRem($iconMarginLeft)};
    `}

  ${({ $iconMarginRight }) =>
    Number.isFinite($iconMarginRight) &&
    css`
      margin-right: ${toRem($iconMarginRight)};
    `}
`;

const BaseTooltipStyles = css`
  padding: ${toRem(8.5)} ${toRem(11)} ${toRem(7.25)}!important;
  border-radius: ${toRem(5.5)}!important;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(11)}!important;
  line-height: 120% !important;
  text-transform: none;
  text-align: center;
  z-index: ${theme.zIndex.toast};
`;

export const TooltipStyles = css`
  background-color: ${theme.color.neutral[900]}!important;
  ${BaseTooltipStyles}
`;

export const Tooltip = styled(ReactTooltip)`
  ${TooltipStyles}
`;

export const TooltipIcon: React.FC<IProps> = ({ children, className, dataCy, icon = 'Tooltip', iconFill, iconMarginLeft, iconMarginRight, iconSize, id, place = 'right', trigger, ...props }) => (
  <>
    {!Utils.isMobileTouchDevice() && (
      <>
        {trigger || <StyledIcon icon={icon} $iconFill={iconFill} $iconMarginLeft={iconMarginLeft} $iconMarginRight={iconMarginRight} $iconSize={iconSize} tooltipId={id} dataCy={dataCy} className={className} />}
        <Tooltip id={id} place={place} className={className} {...props}>
          {children}
        </Tooltip>
      </>
    )}
  </>
);

export const TooltipWrapper = styled.span`
  position: relative;
  display: flex;
  flex-grow: 0;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;

  > * {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
  }
`;
